import Link from '@/components/UI/Link';
import { _notNil } from '@/littledash';
import type { FC, ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import styles from './NavButton.module.scss';

interface NavButtonBaseProps {
  tooltip: string;
  children: ReactNode;
  testId?: string;
}

interface NavButtonLinkProps extends NavButtonBaseProps {
  to: string;
  activeMatch?: Array<string>;
  onClick?: () => void;
  newTab?: boolean;
}

interface NavButtonActionProps extends NavButtonBaseProps {
  onClick: () => void;
  to?: never;
  activeMatch?: never;
  newTab?: never;
}

type NavButtonProps = NavButtonLinkProps | NavButtonActionProps;

/**
 * A single icon button in the sidebar navigation.
 * Renders as a Link when `to` is provided, otherwise as a button.
 */
const NavButton: FC<NavButtonProps> = (props) => {
  const { tooltip, children, testId } = props;
  const location = useLocation();

  const pathMatches = (path: string) =>
    path === '/' ? location.pathname === '/' : location.pathname === path || location.pathname.startsWith(path + '/');

  const isActive = _notNil(props.to) ? pathMatches(props.to) || (props.activeMatch?.some(pathMatches) ?? false) : false;

  const className = `${styles.navButton} ${isActive ? styles.active : ''}`;

  const content = (
    <div
      data-tooltip-content={tooltip}
      data-tooltip-id="global-tooltip-id"
      data-testid={testId}
      data-active={isActive || undefined}
      className={className}
    >
      {children}
    </div>
  );

  if (_notNil(props.to)) {
    return (
      <Link to={props.to} onClick={props.onClick} openTab={props.newTab} className="db tc mv2">
        {content}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={props.onClick}
      className={`db tc mv2 bn bg-transparent pa0 white pointer ${styles.button}`}
    >
      {content}
    </button>
  );
};

export default NavButton;
